<script>
  import { onMount, onDestroy } from 'svelte'
  import { executionInfo, opsStatus, liveDay, liveCalendar, liveBetsDay } from '../../stores.js'
  import { shortAddress } from '../../mode.js'
  import { HORIZON_TITLES } from '../../lib/horizon.js'
  import { fmtBankroll, fmtNum, fmtWinRate, OPS_ERROR_RU } from '../../lib/format.js'
  import { formatPnlPct, monthOfDay, mskToday } from '../../lib/tradingDay.js'
  import { getLiveStrategies, setLiveStrategyEnabled, setLiveDogonEnabled, getLiveBets } from '../../api.js'
  import BetFeed from '../../components/BetFeed.svelte'
  import DayPicker from '../../components/DayPicker.svelte'
  import CoinTradingBar from '../../components/CoinTradingBar.svelte'
  import CesLiveBar from '../../components/CesLiveBar.svelte'
  import { refreshCore } from '../../lib/polling.js'

  const HZ = '15m'
  const STRAT_POLL_MS = 20000
  const BETS_POLL_MS = 30000

  let stratRows = []
  let stratSource = ''
  let stratErr = ''
  let toggleBusy = ''
  let toggleMsg = ''
  let toggleErr = ''
  let dogonEnabled = false
  let dogonKnown = false
  let stratTimer = null
  let betsTimer = null
  let dayBusy = false
  let liveMonth = ''

  function unitActive(units, name) {
    if (!Array.isArray(units)) return null
    const u = units.find((x) => x && (x.unit === name || x.name === name))
    if (!u) return null
    const st = String(u.active_state || u.ActiveState || '').toLowerCase()
    if (st === 'active') return true
    if (st) return false
    return null
  }

  $: ex = $executionInfo
  $: smoke = (ex && ex.smoke) || {}
  // Prefer dedicated /api/live/bets (day-filtered); fall back to execution snapshot.
  $: liveBets = $liveBetsDay || (ex && ex.live_bets) || {}
  $: recent = (liveBets.bets && liveBets.bets.length ? liveBets.bets : []).slice(0, 50)
  $: betCount =
    liveBets.bet_count != null && Number.isFinite(Number(liveBets.bet_count))
      ? Number(liveBets.bet_count)
      : null
  $: openStake =
    liveBets.open_stake != null && Number.isFinite(Number(liveBets.open_stake))
      ? Number(liveBets.open_stake)
      : null
  // Win% zone: always «Сегодня» (MSK) + «Всё время» — not horizon label, not DayPicker day.
  $: liveGlanceToday = {
    wins: liveBets.wins_today != null ? liveBets.wins_today : liveBets.wins,
    losses: liveBets.losses_today != null ? liveBets.losses_today : liveBets.losses,
    win_rate: liveBets.win_rate_today != null ? liveBets.win_rate_today : liveBets.win_rate,
    roi_weighted: liveBets.roi_weighted_today,
  }
  $: liveGlanceAll = {
    wins: liveBets.wins_all,
    losses: liveBets.losses_all,
    win_rate: liveBets.win_rate_all,
    roi_weighted: liveBets.roi_weighted_all,
  }
  const fmtRoiWeighted = (v) => {
    const n = Number(v)
    if (v == null || !Number.isFinite(n)) return null
    const pct = n * 100
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
  }

  $: cal = $liveCalendar || liveBets.calendar || null
  $: calDays = (cal && cal.days) || {}
  $: todayId = (cal && cal.today) || liveBets.today || mskToday()
  $: selectedDay = $liveDay || liveBets.day || todayId
  $: daySummary = liveBets.day_summary || null
  $: dayPctLabel =
    daySummary && daySummary.pnl_pct != null ? formatPnlPct(daySummary.pnl_pct) : ''

  $: live15 = unitActive($opsStatus && $opsStatus.units, 'pmbot-live-15m')
  $: live15Active =
    ex && typeof ex.live_15m_unit_active === 'boolean' ? ex.live_15m_unit_active : live15

  $: onchainBal =
    ex && ex.onchain_pusd_balance != null && Number.isFinite(Number(ex.onchain_pusd_balance))
      ? Number(ex.onchain_pusd_balance)
      : null

  $: onchainPol =
    ex && ex.onchain_pol_balance != null && Number.isFinite(Number(ex.onchain_pol_balance))
      ? Number(ex.onchain_pol_balance)
      : null

  $: bank = (() => {
    if (onchainBal != null) {
      return fmtBankroll({ balance: onchainBal })
    }
    const assumed = ex && ex.smoke_assumed_bankroll
    if (assumed != null && Number.isFinite(Number(assumed))) {
      return fmtBankroll({ balance: Number(assumed) })
    }
    return null
  })()

  $: balanceSub = (() => {
    const parts = []
    const holder =
      (ex && ex.onchain_pusd_holder) ||
      (ex && ex.wallet_address) ||
      null
    if (holder) {
      parts.push(shortAddress(holder))
    }
    if (onchainBal != null && ex && ex.onchain_pusd_ok) {
      const isDeposit =
        ex.onchain_pusd_holder &&
        ex.wallet_address &&
        String(ex.onchain_pusd_holder).toLowerCase() !==
          String(ex.wallet_address).toLowerCase()
      parts.push(isDeposit ? 'deposit pUSD' : 'on-chain pUSD')
    } else if (onchainBal == null && ex && ex.smoke_assumed_bankroll != null) {
      parts.push('из конфига · RPC нет')
    }
    return parts.length ? parts.join(' · ') : 'кошелёк не задан'
  })()

  $: balanceHint = 'свободно для ставок · не портфель Polymarket'

  $: polSub = (() => {
    const parts = []
    if (ex && ex.wallet_address) {
      parts.push(shortAddress(ex.wallet_address))
    }
    if (onchainPol != null && ex && ex.onchain_pol_ok) {
      parts.push('native POL · газ')
      const min = ex.onchain_pol_min != null ? Number(ex.onchain_pol_min) : 0.5
      if (Number.isFinite(min) && onchainPol < min) {
        parts.push(`мало (<${min})`)
      }
    } else {
      parts.push('RPC нет')
    }
    return parts.join(' · ')
  })()

  $: polDisplay =
    onchainPol != null
      ? onchainPol >= 10
        ? onchainPol.toFixed(2)
        : onchainPol.toFixed(4)
      : null

  $: unitLabel =
    live15Active === true
      ? 'unit активен'
      : live15Active === false
        ? 'unit выключен'
        : 'unit —'

  $: armedLabel = ex && ex.live_orders_armed ? 'ордера armed' : 'ордера не armed'

  $: killLabel = ex && ex.kill_switch_active ? 'kill switch' : 'kill выкл'

  $: confirmLabel =
    ex && (ex.live_confirm || live15Active)
      ? `${ex.live_confirm_env || 'LIVE_CONFIRM'}`
      : `${ex?.live_confirm_env || 'LIVE_CONFIRM'} нет`

  $: smokeStatus = String(smoke.status || '')
  $: pausedClean =
    !!(ex && ex.kill_switch_active) || live15Active === false || !!(ex && !ex.live_orders_armed)
  $: statusLine = (() => {
    if (ex && ex.kill_switch_active && live15Active === false) {
      return 'пауза · kill switch · unit выключен · ордера не armed'
    }
    if (ex && ex.kill_switch_active) {
      return ['пауза', killLabel, unitLabel, armedLabel, confirmLabel].filter(Boolean).join(' · ')
    }
    if (live15Active === false) {
      return ['unit выключен', armedLabel, killLabel, confirmLabel].filter(Boolean).join(' · ')
    }
    return [unitLabel, armedLabel, killLabel, confirmLabel].filter(Boolean).join(' · ')
  })()

  $: enabledCount = stratRows.filter(
    (r) => r.enabled !== false && !r.unattributed && r.id !== '_unattributed',
  ).length
  $: rosterCount = stratRows.filter(
    (r) => !r.unattributed && r.id !== '_unattributed',
  ).length
  $: totalBets = stratRows.reduce((s, r) => s + (Number(r.bets) || 0), 0)
  $: totalSettled = stratRows.reduce((s, r) => s + (Number(r.n) || 0), 0)

  function hitTxt(row) {
    if (row == null || row.hit_pct == null || !row.n) return '—'
    return `${row.hit_pct}%`
  }

  function pnlTxt(row) {
    if (row == null || row.pnl == null) return '—'
    const v = Number(row.pnl)
    if (!Number.isFinite(v)) return '—'
    const sign = v > 0 ? '+' : ''
    return `${sign}${v.toFixed(2)}`
  }

  function stakeTxt(row) {
    if (row == null || row.stake == null || !row.bets) return '—'
    const v = Number(row.stake)
    if (!Number.isFinite(v) || v === 0) return '—'
    return `$${v.toFixed(0)}`
  }

  function statusTxt(row) {
    if (row && row.status) return row.status
    if (row && row.enabled === true) return 'вкл'
    if (row && row.enabled === false) return 'выкл'
    return '—'
  }

  function isEnabled(row) {
    return row != null && row.enabled === true
  }

  function patchLocalEnabled(strategyId, enabled) {
    stratRows = stratRows.map((r) =>
      r.id === strategyId
        ? {
            ...r,
            enabled,
            status: enabled ? (r.status === 'выкл' ? 'вкл' : r.status) : 'выкл',
          }
        : r,
    )
  }

  async function loadLiveBets({ day, month } = {}) {
    const d = day || $liveDay || ''
    const m = month || liveMonth || (d ? monthOfDay(d) : monthOfDay(mskToday()))
    try {
      const { res, data } = await getLiveBets({ day: d, month: m, betsLimit: 50 })
      if (res?.ok && data?.ok !== false) {
        liveBetsDay.set(data)
        if (data.calendar) liveCalendar.set(data.calendar)
        if (data.day && !$liveDay) liveDay.set(String(data.day))
        if (data.calendar && data.calendar.month) liveMonth = data.calendar.month
      }
    } catch {
      /* keep last snapshot */
    }
  }

  async function onDayChange(e) {
    const day = e.detail && e.detail.day
    if (!day || day === $liveDay) return
    liveDay.set(day)
    liveMonth = monthOfDay(day)
    dayBusy = true
    try {
      await loadLiveBets({ day, month: liveMonth })
    } finally {
      dayBusy = false
    }
  }

  async function onMonthChange(e) {
    const month = e.detail && e.detail.month
    if (!month) return
    liveMonth = month
    dayBusy = true
    try {
      await loadLiveBets({ day: $liveDay || selectedDay, month })
    } finally {
      dayBusy = false
    }
  }

  async function loadStrategies() {
    try {
      const { res, data } = await getLiveStrategies()
      if (res?.ok && data) {
        stratRows = Array.isArray(data.strategies) ? data.strategies : []
        stratSource = String(data.source || '')
        if (data.dogon && typeof data.dogon.enabled === 'boolean') {
          dogonEnabled = data.dogon.enabled === true
          dogonKnown = true
        }
        if (data.settings_ok === false) {
          const code = data.settings_error || data.error || 'settings_missing'
          const mapped = OPS_ERROR_RU[code] || code
          const path = data.settings_path || (data.path || '')
          stratErr = path ? `${mapped} (${path})` : mapped
          if (!stratRows.length) {
            stratRows = []
          }
        } else if (data.ok === false && data.error) {
          stratErr = String(data.error)
        } else {
          stratErr = ''
        }
      } else if (data?.error) {
        stratErr = String(data.error)
        stratRows = []
      }
    } catch {
      stratErr = 'live_strategies_fetch_failed'
      stratRows = []
    }
  }

  async function onToggle(row) {
    if (!row || !row.id || toggleBusy || stratErr) return
    const next = !isEnabled(row)
    const key = row.id
    toggleBusy = key
    toggleErr = ''
    toggleMsg = ''
    const prev = isEnabled(row)
    patchLocalEnabled(row.id, next)
    try {
      const r = await setLiveStrategyEnabled(row.id, next)
      if (!r.ok) {
        patchLocalEnabled(row.id, prev)
        const code = r.error || 'enable_failed'
        const mapped = OPS_ERROR_RU[code] || code
        const path = r.data && r.data.path
        toggleErr = path ? `${mapped} (${path})` : mapped
        return
      }
      const unit = (r.data && r.data.restart_unit) || 'pmbot-live-15m'
      const restart = (r.data && r.data.restart) || {}
      const skipped = restart.skipped === true
      const restartOk = restart.ok !== false
      const verb = next ? 'Включена' : 'Выключена'
      if (skipped) {
        toggleMsg = `${verb} · yaml записан, live на паузе — restart не делали`
      } else if (restartOk) {
        toggleMsg = `${verb} · restart ${unit}`
      } else {
        toggleMsg = `${verb} в config/live/eth-15m.yaml, но restart ${unit} не удался — проверьте Ops / sudoers`
      }
      await loadStrategies()
    } catch {
      patchLocalEnabled(row.id, prev)
      toggleErr = 'Сбой запроса'
    } finally {
      toggleBusy = ''
    }
  }

  async function onDogonToggle() {
    if (toggleBusy || stratErr || !dogonKnown) return
    const next = !dogonEnabled
    toggleBusy = 'dogon'
    toggleErr = ''
    toggleMsg = ''
    const prev = dogonEnabled
    dogonEnabled = next
    try {
      const r = await setLiveDogonEnabled(next)
      if (!r.ok) {
        dogonEnabled = prev
        const code = r.error || 'enable_failed'
        const mapped = OPS_ERROR_RU[code] || code
        const path = r.data && r.data.path
        toggleErr = path ? `${mapped} (${path})` : mapped
        return
      }
      const unit = (r.data && r.data.restart_unit) || 'pmbot-live-15m'
      const restart = (r.data && r.data.restart) || {}
      const skipped = restart.skipped === true
      const restartOk = restart.ok !== false
      const verb = next ? 'Догон включён' : 'Догон выключен'
      if (skipped) {
        toggleMsg = `${verb} · yaml записан, live на паузе — restart не делали`
      } else if (restartOk) {
        toggleMsg = `${verb} · restart ${unit}`
      } else {
        toggleMsg = `${verb} в config/live/eth-15m.yaml, но restart ${unit} не удался — проверьте Ops / sudoers`
      }
      await loadStrategies()
    } catch {
      dogonEnabled = prev
      toggleErr = 'Сбой запроса'
    } finally {
      toggleBusy = ''
    }
  }

  onMount(() => {
    loadStrategies()
    loadLiveBets()
    stratTimer = setInterval(loadStrategies, STRAT_POLL_MS)
    betsTimer = setInterval(() => loadLiveBets(), BETS_POLL_MS)
  })

  onDestroy(() => {
    if (stratTimer) clearInterval(stratTimer)
    if (betsTimer) clearInterval(betsTimer)
  })
</script>

<div class="dash" aria-label="Живая торговля">
  <section class="dash-zone dash-zone--status" aria-label="Статус live">
    <p
      class="live-status"
      class:is-paused={pausedClean && live15Active !== true}
      data-armed={ex && ex.live_orders_armed ? '1' : '0'}
      data-kill={ex && ex.kill_switch_active ? '1' : '0'}
    >
      <span class="live-status__dot" aria-hidden="true"></span>
      {statusLine}
      {#if smokeStatus === 'armed' || live15Active}
        <span class="live-status__tag">LIVE 15m</span>
      {:else if pausedClean}
        <span class="live-status__tag live-status__tag--pause">ПАУЗА</span>
      {/if}
    </p>
  </section>

  <section class="dash-zone" aria-label="Торговля по монетам">
    <CoinTradingBar
      mode="live"
      compact
      on:changed={() => {
        refreshCore()
      }}
    />
    <CesLiveBar
      on:changed={() => {
        refreshCore()
      }}
    />
  </section>

  <section class="dash-zone" aria-labelledby="zone-balance">
    <header class="dash-zone__head">
      <h2 id="zone-balance">Баланс</h2>
    </header>
    <div class="dash-split">
      <div class="dash-metric" data-horizon={HZ}>
        <p class="dash-metric__label">{HORIZON_TITLES[HZ] || HZ} · pUSD</p>
        <p class="dash-metric__value">
          {bank ? bank.balance : '—'}
          {#if bank}<span class="dash-metric__unit">USD</span>{/if}
        </p>
        <p class="dash-metric__hint">{balanceHint}</p>
        <p class="dash-metric__sub">{balanceSub}</p>
      </div>
      <div class="dash-metric" data-asset="pol">
        <p class="dash-metric__label">Газ · POL</p>
        <p class="dash-metric__value">
          {polDisplay != null ? polDisplay : '—'}
          {#if polDisplay != null}<span class="dash-metric__unit">POL</span>{/if}
        </p>
        <p class="dash-metric__sub">{polSub}</p>
      </div>
    </div>
  </section>

  <section class="dash-zone" aria-labelledby="zone-win">
    <header class="dash-zone__head">
      <h2 id="zone-win">% выигрышей</h2>
    </header>
    <div class="dash-split">
      <div class="dash-metric" data-scope="today">
        <p class="dash-metric__label">Сегодня</p>
        <p class="dash-metric__value dash-metric__value--pct">
          {fmtWinRate(liveGlanceToday.win_rate, {
            wins: liveGlanceToday.wins,
            losses: liveGlanceToday.losses,
          })}
        </p>
        <p class="dash-metric__sub">
          {#if liveGlanceToday.wins != null && liveGlanceToday.losses != null && Number(liveGlanceToday.wins) + Number(liveGlanceToday.losses) > 0}
            {liveGlanceToday.wins} побед · {liveGlanceToday.losses} поражений
            {#if fmtRoiWeighted(liveGlanceToday.roi_weighted)}
              · $-ROI {fmtRoiWeighted(liveGlanceToday.roi_weighted)}
            {/if}
          {:else if liveBets.source === 'attempts'}
            settlements из CSV позже · пока только matched
          {:else}
            пока мало данных
          {/if}
        </p>
      </div>
      <div class="dash-metric" data-scope="all">
        <p class="dash-metric__label">Всё время</p>
        <p class="dash-metric__value dash-metric__value--pct">
          {fmtWinRate(liveGlanceAll.win_rate, {
            wins: liveGlanceAll.wins,
            losses: liveGlanceAll.losses,
          })}
        </p>
        <p class="dash-metric__sub">
          {#if liveGlanceAll.wins != null && liveGlanceAll.losses != null && Number(liveGlanceAll.wins) + Number(liveGlanceAll.losses) > 0}
            {liveGlanceAll.wins} побед · {liveGlanceAll.losses} поражений
            {#if fmtRoiWeighted(liveGlanceAll.roi_weighted)}
              · $-ROI {fmtRoiWeighted(liveGlanceAll.roi_weighted)}
            {/if}
          {:else if liveBets.source === 'attempts'}
            settlements из CSV позже · пока только matched
          {:else}
            пока мало данных
          {/if}
        </p>
      </div>
    </div>
  </section>

  <section class="dash-zone" aria-labelledby="zone-bets">
    <header class="dash-zone__head">
      <h2 id="zone-bets">Ставки</h2>
      <DayPicker
        value={selectedDay}
        today={todayId}
        days={calDays}
        month={(cal && cal.month) || liveMonth}
        disabled={dayBusy}
        on:change={onDayChange}
        on:month={onMonthChange}
      />
    </header>
    <div class="dash-bets-summary">
      <p class="dash-bets-summary__bit">
        <span>15m</span>
        <strong>{betCount != null ? betCount : '—'}</strong>
        {#if openStake != null && selectedDay === todayId}
          <em>в игре {fmtNum(openStake)}</em>
        {/if}
        {#if dayPctLabel}
          <em
            class="mono"
            class:is-up={daySummary && daySummary.pnl_pct > 0}
            class:is-down={daySummary && daySummary.pnl_pct < 0}>{dayPctLabel}</em
          >
        {/if}
      </p>
    </div>
    {#if dayBusy}
      <p class="pm-empty">Загрузка дня…</p>
    {:else}
      <BetFeed items={recent} />
    {/if}
  </section>

  <section class="dash-zone" aria-labelledby="zone-brain">
    <header class="dash-zone__head">
      <h2 id="zone-brain">Мозг и стратегии</h2>
    </header>
    <div class="brain-strats pm-surface" aria-label="Стратегии live 15m">
      <div class="brain-strats__bar">
        <h3 class="brain-strats__title">15m · Live ETH shortlist</h3>
        <p class="brain-strats__sum mono">
          {enabledCount}/{rosterCount || stratRows.length} вкл
          {#if totalBets}
            · {totalBets} ставок
          {/if}
          {#if totalSettled}
            · {totalSettled} settle
          {/if}
          {#if stratSource}
            · {stratSource}
          {/if}
        </p>
      </div>
      <p class="brain-strats__hint">
        ETH 15m live shortlist. Toggle → /var/lib/lunes-crypto/live/eth-15m.yaml
        (тот же файл, что config/live/eth-15m.yaml). Restart только если
        pmbot-live-15m уже запущен. Kill 33% B_day.
      </p>
      {#if dogonKnown}
        <div class="brain-dogon" data-testid="live-dogon-toggle">
          <span class="brain-strats__name">Догон</span>
          <label class="brain-switch" title={dogonEnabled ? 'Выключить догон' : 'Включить догон'}>
            <input
              type="checkbox"
              role="switch"
              aria-label="Догон live 15m"
              checked={dogonEnabled}
              disabled={!!toggleBusy || !!stratErr}
              on:change={onDogonToggle}
            />
            <span class="brain-switch__ui" aria-hidden="true"></span>
            <span class="brain-switch__txt">{dogonEnabled ? 'вкл' : 'выкл'}</span>
          </label>
        </div>
      {/if}
      {#if toggleMsg}
        <p class="brain-toggle-msg">{toggleMsg}</p>
      {/if}
      {#if toggleErr}
        <p class="pm-error">{toggleErr}</p>
      {/if}
      {#if stratErr}
        <p class="pm-error">{stratErr}</p>
      {/if}

      {#if stratRows.length === 0}
        <p class="brain-strats__empty">Список стратегий live 15m пока пуст.</p>
      {:else}
        <div class="brain-strats__table-wrap">
          <table class="brain-strats__table">
            <thead>
              <tr>
                <th scope="col" class="tog">Вкл</th>
                <th scope="col">Стратегия</th>
                <th scope="col" class="num">Win %</th>
                <th scope="col" class="num">Ставки</th>
                <th scope="col" class="num">Settle</th>
                <th scope="col" class="num">Stake</th>
                <th scope="col" class="num">PnL</th>
                <th scope="col" class="stat">Статус</th>
              </tr>
            </thead>
            <tbody>
              {#each stratRows as row}
                <tr class:is-quiet={!row.bets} class:is-off={!isEnabled(row)}>
                  <td class="tog">
                    {#if row.unattributed || row.id === '_unattributed'}
                      <span class="brain-strats__id mono">—</span>
                    {:else}
                      <label class="brain-switch" title={isEnabled(row) ? 'Выключить' : 'Включить'}>
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label={`${row.name_ru || row.id} live 15m`}
                          checked={isEnabled(row)}
                          disabled={!!toggleBusy || !!stratErr}
                          on:change={() => onToggle(row)}
                        />
                        <span class="brain-switch__ui" aria-hidden="true"></span>
                        <span class="brain-switch__txt">{isEnabled(row) ? 'вкл' : 'выкл'}</span>
                      </label>
                    {/if}
                  </td>
                  <td>
                    <span class="brain-strats__name">{row.name_ru || row.id}</span>
                    <span class="brain-strats__id mono">{row.id}</span>
                  </td>
                  <td class="num mono" class:is-ok={row.hit_pct != null && row.hit_pct >= 55}
                    >{hitTxt(row)}</td
                  >
                  <td class="num mono">{row.bets ?? 0}</td>
                  <td class="num mono muted">{row.n ?? 0}</td>
                  <td class="num mono muted">{stakeTxt(row)}</td>
                  <td
                    class="num mono"
                    class:is-ok={row.pnl > 0}
                    class:is-bad={row.pnl < 0}>{pnlTxt(row)}</td
                  >
                  <td class="stat">
                    <span
                      class="brain-pill"
                      class:is-kill={statusTxt(row) === 'kill'}
                      class:is-off={statusTxt(row) === 'выкл'}
                      >{statusTxt(row)}</span
                    >
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  </section>
</div>

<style>
  .dash {
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
  }
  .dash-zone {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
  }
  .dash-zone + .dash-zone {
    padding-top: 1.5rem;
    border-top: 1px solid var(--pm-line);
  }
  .dash-zone--status {
    padding-top: 0;
    border-top: 0;
  }
  .dash-zone__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
  }
  .dash-zone__head h2 {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .live-status {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem 0.65rem;
    font-size: 0.82rem;
    color: var(--pm-ink-soft);
    line-height: 1.4;
  }
  .live-status__dot {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: var(--pm-ink-mute);
    flex-shrink: 0;
  }
  .live-status[data-armed='1'] .live-status__dot {
    background: var(--pm-mode-live-fg);
    box-shadow: 0 0 0 2px rgba(var(--pm-live-rgb), 0.25);
  }
  .live-status[data-kill='1'] .live-status__dot {
    background: var(--pm-bad);
    box-shadow: none;
  }
  .live-status__tag {
    font-family: var(--pm-mono);
    font-size: 0.62rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pm-mode-live-fg);
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    border: 1px solid rgba(var(--pm-live-rgb), 0.35);
    background: rgba(var(--pm-live-rgb), 0.06);
  }
  .live-status__tag--pause {
    color: var(--pm-bad);
    border-color: rgba(192, 57, 43, 0.35);
    background: rgba(192, 57, 43, 0.08);
  }
  .live-status.is-paused {
    color: var(--pm-ink-mute);
  }
  .dash-split {
    display: grid;
    gap: 1rem 1.5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    .dash-split {
      grid-template-columns: 1fr;
    }
  }
  .dash-metric__label {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    color: var(--pm-ink-soft);
  }
  .dash-metric__value {
    margin: 0;
    font-size: clamp(1.85rem, 4vw, 2.45rem);
    font-weight: 700;
    letter-spacing: -0.045em;
    line-height: 1;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .dash-metric__value--pct {
    letter-spacing: -0.03em;
  }
  .dash-metric__unit {
    margin-left: 0.25rem;
    font-size: 0.38em;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: var(--pm-ink-mute);
    vertical-align: 0.35em;
  }
  .dash-metric__sub {
    margin: 0.45rem 0 0;
    font-size: 0.82rem;
    color: var(--pm-ink-mute);
    font-variant-numeric: tabular-nums;
  }
  .dash-metric__hint {
    margin: 0.35rem 0 0;
    font-size: 0.78rem;
    color: var(--pm-ink-soft);
  }
  .dash-bets-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem 1.25rem;
    margin-bottom: 0.85rem;
  }
  .dash-bets-summary__bit {
    margin: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: var(--pm-ink-mute);
  }
  .dash-bets-summary__bit span {
    font-family: var(--pm-mono);
    font-size: 0.68rem;
    letter-spacing: 0.08em;
  }
  .dash-bets-summary__bit strong {
    font-size: 1rem;
    font-weight: 650;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .dash-bets-summary__bit em {
    font-style: normal;
    color: var(--pm-ink-mute);
  }
  .dash-bets-summary__bit em.is-up {
    color: var(--pm-ok);
  }
  .dash-bets-summary__bit em.is-down {
    color: var(--pm-bad);
  }

  .brain-strats {
    padding: 0.75rem 0.85rem 0.85rem;
  }
  .brain-strats__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.35rem 1rem;
    margin-bottom: 0.35rem;
  }
  .brain-strats__title {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .brain-strats__sum {
    margin: 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .brain-strats__hint {
    margin: 0 0 0.55rem;
    font-size: 0.68rem;
    color: var(--pm-ink-mute);
    line-height: 1.35;
  }
  .brain-dogon {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0 0 0.65rem;
    padding: 0.35rem 0;
  }
  .brain-strats__empty {
    margin: 0;
    font-size: 0.85rem;
    color: var(--pm-ink-mute);
  }
  .brain-toggle-msg {
    margin: 0 0 0.45rem;
    font-size: 0.78rem;
    color: var(--pm-ok, #1a7f4e);
  }
  .brain-strats__table-wrap {
    overflow: visible;
    max-height: none;
  }
  .brain-strats__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
  }
  .brain-strats__table th {
    text-align: left;
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
    padding: 0.25rem 0.45rem 0.45rem 0;
    border-bottom: 1px solid var(--pm-line);
    position: sticky;
    top: 0;
    background: var(--pm-paper);
    z-index: 1;
  }
  .brain-strats__table th.num,
  .brain-strats__table td.num {
    text-align: right;
  }
  .brain-strats__table th.stat,
  .brain-strats__table td.stat {
    text-align: right;
    padding-right: 0;
  }
  .brain-strats__table th.tog,
  .brain-strats__table td.tog {
    width: 4.5rem;
    padding-right: 0.35rem;
    vertical-align: middle;
  }
  .brain-strats__table td {
    padding: 0.42rem 0.45rem 0.42rem 0;
    border-bottom: 1px solid var(--pm-line);
    vertical-align: baseline;
  }
  .brain-strats__table tr.is-quiet td {
    opacity: 0.72;
  }
  .brain-strats__table tr.is-off td {
    opacity: 0.55;
  }
  .brain-strats__name {
    display: block;
    font-weight: 550;
    color: var(--pm-ink);
    line-height: 1.25;
  }
  .brain-strats__id {
    display: block;
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
    margin-top: 0.1rem;
  }
  .brain-strats__table td.is-ok {
    color: var(--pm-ok);
  }
  .brain-strats__table td.is-bad {
    color: var(--pm-bad);
  }
  .brain-strats__table td.muted {
    color: var(--pm-ink-mute);
  }
  .brain-pill {
    display: inline-block;
    font-size: 0.64rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: lowercase;
    padding: 0.12rem 0.4rem;
    border-radius: 999px;
    border: 1px solid var(--pm-line);
    color: var(--pm-ink-soft);
  }
  .brain-pill.is-kill {
    color: var(--pm-bad);
    border-color: rgba(192, 57, 43, 0.35);
  }
  .brain-pill.is-off {
    color: var(--pm-ink-mute);
  }
  .brain-switch {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    user-select: none;
  }
  .brain-switch input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
  }
  .brain-switch__ui {
    width: 1.7rem;
    height: 0.95rem;
    border-radius: 999px;
    background: var(--pm-line);
    border: 1px solid var(--pm-border);
    position: relative;
    flex: 0 0 auto;
    transition: background 0.15s ease;
  }
  .brain-switch__ui::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: var(--pm-paper);
    border: 1px solid var(--pm-border);
    transition: transform 0.15s ease;
  }
  .brain-switch input:checked + .brain-switch__ui {
    background: rgba(26, 127, 78, 0.35);
    border-color: rgba(26, 127, 78, 0.45);
  }
  .brain-switch input:checked + .brain-switch__ui::after {
    transform: translateX(0.7rem);
    border-color: rgba(26, 127, 78, 0.5);
  }
  .brain-switch input:disabled + .brain-switch__ui {
    opacity: 0.5;
  }
  .brain-switch__txt {
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: lowercase;
    color: var(--pm-ink-mute);
    min-width: 1.6rem;
  }
</style>
